import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaPlus, FaSearch, FaEdit } from 'react-icons/fa';
import { getManualServiceProviders } from '../services/subscriberWalletService';
import { listMedicines, editMedicine } from '../services/medicineCatalogService';
import { getCurrentRole } from '../utils/medicineHelpers';
import EditMedicineModal from '../components/medicine/EditMedicineModal';

const AddMedicine = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentRole = getCurrentRole();
  
  // Medicine management state - initialize from URL params
  const [managementProviders, setManagementProviders] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState(() => searchParams.get('vendor_id') || '');
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const [medicines, setMedicines] = useState([]);
  const [pagination, setPagination] = useState({ 
    page: parseInt(searchParams.get('page') || '1', 10), 
    items: 25, 
    total: 0, 
    total_pages: 0 
  });
  const [loadingMedicines, setLoadingMedicines] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [mounted, setMounted] = useState(false);
  const fetchInProgressRef = useRef(false);
  const providersFetchedRef = useRef(false);

  // Set mounted state
  useEffect(() => {
    setMounted(true);
  }, []);

  // Track if initial load from URL is complete
  const initialLoadRef = useRef(false);

  // Fetch providers for management (only once)
  useEffect(() => {
    const fetchProviders = async () => {
      if (!currentRole || (currentRole !== 'Ops' && currentRole !== 'SuperAdmin')) return;
      
      // Prevent duplicate calls (especially in StrictMode)
      if (providersFetchedRef.current) return;
      providersFetchedRef.current = true;
      
      try {
        const response = await getManualServiceProviders(currentRole, 'medicine');
        if (response && response.metadata && response.metadata.api_status) {
          const providers = response.msg?.providers || [];
          setManagementProviders(providers);
          
          // If URL has vendor_id, load medicines after providers are loaded
          const urlVendorId = searchParams.get('vendor_id');
          if (urlVendorId && !initialLoadRef.current) {
            initialLoadRef.current = true;
            setSelectedVendorId(urlVendorId);
            const urlPage = parseInt(searchParams.get('page') || '1', 10);
            const urlSearch = searchParams.get('search') || '';
            fetchMedicinesList(urlVendorId, urlSearch, urlPage, 25);
          }
        }
      } catch (error) {
        console.error('Error fetching providers:', error);
        // Reset on error so it can retry if needed
        providersFetchedRef.current = false;
      }
    };

    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRole, searchParams]);

  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedVendorId) params.set('vendor_id', selectedVendorId);
    if (pagination.page > 1) params.set('page', pagination.page.toString());
    if (searchTerm.trim()) params.set('search', searchTerm.trim());
    setSearchParams(params, { replace: true });
  }, [selectedVendorId, pagination.page, searchTerm, setSearchParams]);
      
  // Fetch medicines when vendor is selected (skip if initial load from URL)
  useEffect(() => {
    if (!selectedVendorId) {
      setMedicines([]);
      setPagination(prev => ({ ...prev, page: 1, total: 0, total_pages: 0 }));
      setSearchInput('');
      setSearchTerm('');
      return;
    }

    // Skip if this is the initial load from URL (handled in providers fetch)
    if (initialLoadRef.current && searchParams.get('vendor_id') === selectedVendorId) {
      initialLoadRef.current = false; // Reset after first load
      return;
    }

    // New vendor selection - reset search and page
    setSearchInput('');
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchMedicinesList(selectedVendorId, '', 1, 25);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendorId]);

  // Debounced search (only when searchInput changes, not when vendor changes)
  useEffect(() => {
    if (!selectedVendorId) return;
    
    if (!searchInput.trim()) {
      // If search is cleared, reset to page 1
      if (searchTerm) {
        setSearchTerm('');
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchMedicinesList(selectedVendorId, '', 1, 25);
      }
      return;
    }

    const timeout = setTimeout(() => {
      const searchValue = searchInput.trim();
      setSearchTerm(searchValue);
      setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 on new search
      fetchMedicinesList(selectedVendorId, searchValue, 1, 25);
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);


  // Fetch medicines list
  const fetchMedicinesList = useCallback(async (vendorId, search = '', page = 1, items = 25) => {
    if (!vendorId || !currentRole) {
      setMedicines([]);
      return;
    }

    // Create a unique key for this request to prevent duplicate calls
    const requestKey = `${vendorId}-${search}-${page}-${items}`;
    if (fetchInProgressRef.current === requestKey) {
      return;
    }

    fetchInProgressRef.current = requestKey;
    setLoadingMedicines(true);
    
    try {
      const response = await listMedicines(currentRole, vendorId, { search, page, items });
      if (response && response.metadata && response.metadata.api_status) {
        setMedicines(response.msg?.items || []);
        const apiPagination = response.msg?.pagination || {};
        setPagination({ 
          page: apiPagination.page || page, 
          items: apiPagination.items || items, 
          total: apiPagination.total || 0, 
          total_pages: apiPagination.total_pages || 0 
        });
      } else {
        setMedicines([]);
        setPagination({ page: 1, items: 25, total: 0, total_pages: 0 });
        setErrorMsg(`Error: ${response?.msg?.error || 'Failed to load medicines'}`);
        setTimeout(() => setErrorMsg(''), 5000);
      }
    } catch (error) {
      console.error('Error fetching medicines:', error);
      setMedicines([]);
      setPagination({ page: 1, items: 25, total: 0, total_pages: 0 });
      
      const status = error.status || error.message.match(/\b(\d{3})\b/)?.[1];
      const errorMessages = {
        '500': 'Server error (500): Unable to load medicines. ',
        '404': 'Endpoint not found (404): The medicine list endpoint may not be available for this role.',
        '403': 'Access forbidden (403): You may not have permission to view medicines.'
      };
      
      setErrorMsg(errorMessages[status] || error.message.match(/API Error \d+[^:]*:\s*(.+)/)?.[1] || error.message || 'Error loading medicines');
      setTimeout(() => setErrorMsg(''), 8000);
    } finally {
      setLoadingMedicines(false);
      // Only clear if this is still the current request (prevents race conditions)
      if (fetchInProgressRef.current === requestKey) {
        fetchInProgressRef.current = null;
      }
    }
  }, [currentRole]);

  // Handle vendor selection for management
  const handleVendorChange = useCallback((vendorId) => {
    setSelectedVendorId(vendorId);
    setSearchInput('');
    setSearchTerm('');
    // Reset to page 1 when vendor changes
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Handle edit medicine
  const handleEditClick = useCallback((medicine) => {
    setEditingMedicine(medicine);
    setShowEditModal(true);
  }, []);

  // Handle edit submit
  const handleEditSubmit = useCallback(async (medicineId, formData) => {
    const response = await editMedicine(currentRole, medicineId, formData);
    if (response?.metadata?.api_status) {
      setSuccessMsg('Medicine updated successfully!');
      setShowEditModal(false);
      setEditingMedicine(null);
      fetchMedicinesList(selectedVendorId, searchTerm, pagination.page, pagination.items);
      setTimeout(() => setSuccessMsg(''), 5000);
    } else {
      throw new Error(response?.msg?.error || 'Failed to update medicine');
    }
  }, [currentRole, selectedVendorId, searchTerm, pagination.page, pagination.items, fetchMedicinesList]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchMedicinesList(selectedVendorId, searchTerm, newPage, pagination.items);
  }, [selectedVendorId, searchTerm, pagination.items, fetchMedicinesList]);

  // Calculate pagination values
  const totalPages = pagination.total_pages || Math.ceil(pagination.total / pagination.items) || 1;
  const startItem = ((pagination.page - 1) * pagination.items) + 1;
  const endItem = Math.min(pagination.page * pagination.items, pagination.total);
  const isFirstPage = pagination.page === 1;
  const isLastPage = pagination.page >= totalPages;


  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        {/* Back button */}
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate('/subscriber-wallet')} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>


        {/* Medicine Management Section */}
        {(currentRole === 'Ops' || currentRole === 'SuperAdmin') && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-gray-900">Medicine Management</h2>
              <button
                onClick={() => navigate('/subscriber-wallet/add-medicine/form')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border transition-colors bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:border-blue-700"
                title="Add a new medicine to the catalog"
              >
                <FaPlus className="w-4 h-4" />
                Add Medicine
              </button>
          </div>

            {/* Error and Success Messages */}
        {errorMsg && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{errorMsg}</p>
          </div>
        )}

            {successMsg && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-800">{successMsg}</p>
              </div>
            )}
            
            {/* Service Provider Dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Service Provider <span className="text-red-500">*</span>
            </label>
            <select
                value={selectedVendorId}
                onChange={(e) => handleVendorChange(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
                <option value="">-- Select Service Provider --</option>
                {managementProviders.map(provider => (
                  <option key={provider.id} value={provider.id}>
                    {provider.name || provider.code || provider.id}
                </option>
              ))}
            </select>
          </div>

            {/* Search Bar - Only show when vendor is selected */}
            {selectedVendorId && (
              <>
                <div className="mb-4">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
                      type="text"
                      placeholder="Search by medicine name or ID..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
          </div>

                {/* Medicines List Table */}
                {loadingMedicines ? (
                  <div className="text-center py-8 text-gray-500">Loading medicines...</div>
                ) : medicines.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">Medicine Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">ID</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">MRP</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">AyushPay MRP</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">Unit</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">Status</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase border-b">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {medicines.map(medicine => (
                            <tr key={medicine.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{medicine.service_item_name}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{medicine.service_item_id}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">₹{medicine.partner_price?.toFixed(2) || '0.00'}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">₹{medicine.partner_discount?.toFixed(2) || '0.00'}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{medicine.unit}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  medicine.active 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {medicine.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <button
                                  onClick={() => handleEditClick(medicine)}
                                  className="flex items-center gap-1 px-3 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                >
                                  <FaEdit className="w-4 h-4" />
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
          </div>

                    {/* Pagination */}
                    {pagination.total > 0 && (
                      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          Showing {startItem}-{endItem} of {pagination.total}
          </div>
                        <div className="flex items-center space-x-2">
                          <button
                            disabled={isFirstPage}
                            onClick={() => handlePageChange(pagination.page - 1)}
                            className={`px-3 py-1 border rounded-md text-sm ${isFirstPage ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                          >
                            Prev
                          </button>
                          {Array.from({ length: totalPages }, (_, i) => {
                            const pageNum = i + 1;
                            const shouldShow = pageNum === 1 || pageNum === totalPages || (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2);
                            const showEllipsis = !shouldShow && (pageNum === pagination.page - 3 || pageNum === pagination.page + 3);
                            
                            return showEllipsis ? (
                              <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>
                            ) : shouldShow ? (
            <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-1 border rounded-md text-sm ${pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
            >
                                {pageNum}
            </button>
                            ) : null;
                          })}
            <button
                            disabled={isLastPage}
                            onClick={() => handlePageChange(pagination.page + 1)}
                            className={`px-3 py-1 border rounded-md text-sm ${isLastPage ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
            >
                            Next
            </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {searchTerm ? 'No medicines found matching your search.' : 'No medicines found for this provider.'}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Edit Medicine Modal */}
        <EditMedicineModal
          mounted={mounted}
          show={showEditModal}
          medicine={editingMedicine}
          onClose={() => {
            setShowEditModal(false);
            setEditingMedicine(null);
          }}
          onSubmit={handleEditSubmit}
        />
      </div>
    </div>
  );
};

export default AddMedicine;
